import { Pressable } from 'react-native';

import { CaseFooter } from '@/components/chat/bubbles/ClinicalCaseBlock';
import { Box } from '@/components/ui/box';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useClinicalCaseHeaderStore } from '@/services/chat/clinicalCaseHeader';
import { getAccessibilityProps } from '@/util/accessibilityProps';

/**
 * The full case summary dropped over the thread from the header line, so an expert can read it
 * while replying without losing their place. It sits over the list only: the composer is not
 * part of it and does not move. The scrim and the header line both close it.
 */
export function ClinicalCasePeek({
  conversationId,
  topInset,
}: {
  conversationId: string;
  topInset: number;
}) {
  const peekId = useClinicalCaseHeaderStore((state) => state.peekId);

  const setPeekId = useClinicalCaseHeaderStore((state) => state.setPeekId);

  const header = useClinicalCaseHeaderStore((state) => state.header);

  const requestScroll = useClinicalCaseHeaderStore((state) => state.requestScrollToOpening);

  if (peekId !== conversationId || header?.conversationId !== conversationId) {
    return null;
  }

  const close = () => setPeekId(null);

  return (
    <Box
      className="absolute inset-x-0 bottom-0 z-10"
      style={{ top: topInset }}
      testID="clinical-case-peek">
      <VStack className="gap-1 border-b border-outline-200 bg-background-0 px-4 py-3">
        <Text size="sm" className="font-body font-semibold text-typography-900">
          Case summary
        </Text>
        <Text size="sm" className="font-body leading-relaxed text-typography-800">
          {header.line}
        </Text>
        <CaseFooter
          conversationId={conversationId}
          source="peek"
          onGoToMessage={
            header.canScrollToOpening
              ? () => {
                  close();

                  requestScroll(conversationId);
                }
              : undefined
          }
        />
      </VStack>
      <Pressable
        onPress={close}
        className="flex-1"
        {...getAccessibilityProps({ label: 'Close case summary', role: 'button' })}>
        <Box className="flex-1 bg-background-950 opacity-25" />
      </Pressable>
    </Box>
  );
}
